/**
 * Everything the web app may send to PostHog: each event by name, and each
 * property it may carry. The send hook drops whatever is not named here, so a
 * new event, or a property a new SDK version adds, stays in the browser until
 * a reviewed change names it in this file.
 */

/** The events the app reports itself, each with the properties it carries. */
export const APP_EVENTS = {
  // Reported by hand when the router lands on a new path; the SDK adds the page.
  $pageview: [],
  sign_in_started: [],
  signed_out: [],
  draft_created: [],
  client_created: [],
  file_uploaded: ["file_type"],
  file_retried: ["file_type"],
  file_deleted: [],
  figure_uploaded: [],
  figure_layout_changed: ["change"],
  invention_element_added: [],
  invention_element_confirmed: [],
  invention_extraction_started: [],
  invention_settled: [],
  novelty_run_started: [],
  novelty_committed: [],
  policies_accepted: [],
  dd_framework_confirmed: [],
  client_error: ["where", "status"],
  write_stalled: ["where", "control", "waiting", "oldest_ms"],
  feedback_submitted: ["kind", "ticket_id"],
} as const satisfies Record<string, readonly string[]>;

export type AppEvent = keyof typeof APP_EVENTS;

export type AppEventProperty<E extends AppEvent> =
  (typeof APP_EVENTS)[E][number];

/**
 * The events the SDK builds itself that something here depends on. Every
 * other one is dropped, including autocapture, rage and dead clicks, heatmaps,
 * web vitals, page leaves, feature flag calls, surveys, product tours and the
 * Support widget's own usage events. A feedback ticket is a request of its
 * own, not an event, so dropping those does not stop one being made.
 */
const SDK_EVENTS = {
  // identify() joins the visit before sign-in to the person who signed in.
  $identify: ["$anon_distinct_id"],
  // What identify() sends for someone this browser already knows. The person
  // properties ride in it under `$set`, held to PERSON_PROPERTIES below.
  $set: [],
  // Error tracking. Each entry of the list keeps only EXCEPTION_PARTS, and its
  // message only when it is an API failure's status line.
  $exception: ["$exception_list", "$exception_level"],
  // Session replay, off for the pilot. Its frames are masked as they are
  // recorded, so they pass as they come.
  $snapshot: ["$snapshot_data", "$snapshot_bytes", "$snapshot_host"],
} as const satisfies Record<string, readonly string[]>;

/** What PostHog needs to accept an event, attribute it and join it to a visit. */
const DELIVERY = [
  "token",
  "distinct_id",
  "$insert_id",
  "$time",
  "$lib",
  "$lib_version",
  "$device_id",
  "$session_id",
  "$window_id",
  // Without it, every anonymous event would make a person profile.
  "$process_person_profile",
  "$is_identified",
  "$cookieless_mode",
];

/**
 * Where an event happened and in what browser. A view says nothing without its
 * page, and an error often reproduces only in the browser that raised it. URLs
 * reach this list already cut at the query string.
 */
const PAGE_AND_BROWSER = [
  "$current_url",
  "$host",
  "$pathname",
  "$referrer",
  "$referring_domain",
  "$browser",
  "$browser_version",
  "$os",
  "$os_version",
  "$device_type",
  "$viewport_width",
  "$viewport_height",
];

/** What the app adds to every event: the stage, staff or not, and no location lookup. */
const STAMPS = ["praxis_environment", "is_internal", "$geoip_disable"];

const ON_EVERY_EVENT = [...DELIVERY, ...PAGE_AND_BROWSER, ...STAMPS];

/** Each approved event, with every property it may carry. */
export const APPROVED_EVENTS: ReadonlyMap<
  string,
  ReadonlySet<string>
> = new Map(
  Object.entries({ ...APP_EVENTS, ...SDK_EVENTS }).map(([event, own]) => [
    event,
    new Set([...ON_EVERY_EVENT, ...own]),
  ]),
);

/** The person properties `$set` and `$set_once` may carry, on any event. */
export const PERSON_PROPERTIES: ReadonlySet<string> = new Set(["is_internal"]);

/** What each entry of `$exception_list` keeps besides its message: all of it names code. */
export const EXCEPTION_PARTS: ReadonlySet<string> = new Set([
  "type",
  "mechanism",
  "stacktrace",
]);
